import { useLanguage } from "@/context/LanguageContext";
import { useId } from "react";
import { VEHICLE_TARIFFS, TARIFF_POLICY } from "@/config/tariffs";

const fuelGroups = Array.from(
  VEHICLE_TARIFFS.reduce((groups, vehicle) => {
    const key = vehicle.fuelKmPerLitre;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        fuelKmPerLitre: vehicle.fuelKmPerLitre,
        vehicles: [],
      });
    }
    groups.get(key).vehicles.push(vehicle.name);
    return groups;
  }, new Map()).values()
).sort((a, b) => b.fuelKmPerLitre - a.fuelKmPerLitre);

export const FuelRatioGroups = () => {
  const { t } = useLanguage();
  return (
  <dl className="grid gap-3 sm:grid-cols-2" data-testid="fare-fuel-ratio-groups">
    {fuelGroups.map((group) => (
      <div
        key={group.key}
        className="rounded-xl border border-slate-200 bg-white p-4"
        data-testid={`fare-fuel-ratio-${group.key}`}
      >
        <dt className="font-bold text-slate-900">
          {t("1 litre per {km} KM", { km: group.fuelKmPerLitre })}
        </dt>
        <dd className="mt-2 text-sm leading-relaxed text-slate-600">
          {group.vehicles.join(", ")}
        </dd>
      </div>
    ))}
  </dl>
  );
};

export const FareTerms = ({ compact = false }) => {
  const { t } = useLanguage();
  const headingId = useId();
  const policy = TARIFF_POLICY;

  const fuelDetails = (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-slate-600">
        {t("These ratios determine the local fuel bill; they are billing terms, not vehicle mileage claims. Use the selected vehicle’s billing ratio and the applicable fuel price per litre.")}
      </p>
      <FuelRatioGroups />
      <div className="rounded-xl bg-[#071426] p-4 text-white" data-testid="fare-fuel-formula">
        <p className="text-xs font-bold uppercase tracking-wider text-amber-400">{t("Local fuel calculation")}</p>
        <p className="mt-2 text-sm font-semibold leading-relaxed">
          {t("Fuel charge = (greater of actual KM or {min_km} KM) ÷ billing KM per litre × applicable fuel price per litre.", { min_km: policy.minFuelKm })}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-300">
          {t("First choose the greater distance, then divide by the vehicle’s ratio to calculate litres. Multiply those litres by the applicable fuel price. Add the fuel charge to the local base hire.")}
        </p>
      </div>
    </div>
  );

  return (
    <section
      aria-labelledby={headingId}
      className={`rounded-2xl border border-slate-200 bg-white ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8"}`}
      data-testid={compact ? "fare-terms-compact" : "fare-terms"}
    >
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">{t("Before you book")}</p>
      <h2 id={headingId} className={`mt-2 font-extrabold text-slate-900 ${compact ? "text-xl" : "text-2xl sm:text-3xl"}`}>
        {t("Fare terms & fuel billing")}
      </h2>
      <div className={`mt-6 grid gap-5 ${compact ? "" : "md:grid-cols-2"}`}>
        <div className="rounded-xl bg-slate-50 p-5" data-testid="fare-local-terms">
          <h3 className="text-lg font-bold text-slate-900">{t("Local hire: {km} KM / {hours} hours", { km: policy.localKm, hours: policy.localHours })}</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            {t("Pay the selected vehicle’s local base hire plus fuel. Fuel is billed for at least {min_fuel_km} KM. Beyond {local_km} KM, there is no separate per-KM hire charge; fuel is billed for actual running. After {hours} hours, the vehicle’s extra-hour hire rate applies.", {
              min_fuel_km: policy.minFuelKm,
              local_km: policy.localKm,
              hours: policy.localHours,
            })}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 p-5" data-testid="fare-outstation-terms">
          <h3 className="text-lg font-bold text-slate-900">{t("Outstation: {km} KM per day minimum", { km: policy.outstationMinKmPerDay })}</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            {t("The daily minimum applies to every vehicle. Outstation per-KM rates include fuel.")}
            {policy.garageToGarage && t(" Running is calculated garage-to-garage.")}
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="fare-tax-and-extras">
        <h3 className="font-bold text-amber-950">{t("{gst}% GST and additional charges", { gst: policy.gstPercent })}</h3>
        <p className="mt-2 text-sm leading-relaxed text-amber-900">
          {t("Published rates exclude {gst}% GST. Additional charges: {extras}.", { gst: policy.gstPercent, extras: policy.extras.map(extra => t(extra)).join(", ") })}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-amber-900">
          {t("The vehicle’s listed night charge applies per applicable night to both local and outstation hire.")}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-amber-900">
          {t("Other road expenses are also extra for local Tempo Traveller hire.")}
        </p>
      </div>

      {compact ? (
        <details className="mt-5 rounded-xl border border-slate-200 p-4" data-testid="fare-fuel-details">
          <summary className="cursor-pointer rounded font-bold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2">
            {t("Local fuel ratios and calculation")}
          </summary>
          <div className="mt-4">{fuelDetails}</div>
        </details>
      ) : (
        <div className="mt-7" data-testid="fare-fuel-details">
          <h3 className="mb-4 text-xl font-bold text-slate-900">{t("Local fuel ratios by vehicle")}</h3>
          {fuelDetails}
        </div>
      )}

      <p className="mt-5 text-sm leading-relaxed text-slate-600" data-testid="fare-confirmation">
        {t("Confirm the full itinerary, vehicle availability, applicable night charges and final quotation before booking.")}
      </p>
    </section>
  );
};

export default FareTerms;
